import React, { useEffect, useRef, useState } from 'react';
import { GOOGLE_CLIENT_ID } from '../../../../config/googleOAuth';
import { renderGoogleSignInButton } from '../../../../services/googleIdentity';
import { useChatStrings } from '../../stringsContext';
import { WaitRing } from '../Icons';

interface GoogleAuthSectionProps {
   /** Receives the Google-signed ID token from "Sign in with Google"; the API verifies it. */
   onGoogleAuth: (idToken: string) => Promise<void>;
   isLoading: boolean;
}

/**
 * Google draws its own button, once, into the slot below. The slot holds the pill's size from the first paint so
 * the button's arrival does not move anything, and it is never redrawn (a theme switch leaves it alone).
 */
const GoogleAuthSection: React.FC<GoogleAuthSectionProps> = ({ onGoogleAuth, isLoading }) => {
   const strings = useChatStrings();
   const buttonRef = useRef<HTMLDivElement>(null);
   const onGoogleAuthRef = useRef(onGoogleAuth);
   onGoogleAuthRef.current = onGoogleAuth;
   const [isSigningIn, setIsSigningIn] = useState(false);
   const [unavailable, setUnavailable] = useState(!GOOGLE_CLIENT_ID);

   useEffect(() => {
      const parent = buttonRef.current;
      if (!parent || !GOOGLE_CLIENT_ID) return;

      renderGoogleSignInButton(
         parent,
         GOOGLE_CLIENT_ID,
         async (idToken) => {
            setIsSigningIn(true);
            try {
               await onGoogleAuthRef.current(idToken);
            } finally {
               setIsSigningIn(false);
            }
         },
         Math.min(400, Math.max(200, Math.floor(parent.getBoundingClientRect().width)))
      ).catch((error) => {
         console.error('Sign in with Google is unavailable:', error);
         setUnavailable(true);
      });
   }, []);

   if (unavailable) return <p className="cx-signin__body">{strings.signInUnavailable}</p>;

   const busy = isLoading || isSigningIn;
   return (
      <div className="cx-signin__google-wrap">
         <div ref={buttonRef} className={`cx-signin__google${busy ? ' cx-signin__google--busy' : ''}`} />
         {isSigningIn && (
            <p className="cx-wait" role="status" aria-live="polite">
               <WaitRing className="cx-ring" />
               <span>{strings.signingIn}</span>
            </p>
         )}
      </div>
   );
};

export default GoogleAuthSection;
